<template>
  <div class="input-wrapper" @click="inputRef?.focus()" :class="[size, { error: formField.hasError.value }]">
    <slot name="prefix"></slot>
    <input v-bind="inputArgs" :value="modelValue" :id="formField.id" @input="onChange" ref="inputRef" />
    <slot name="suffix"></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useAttrs } from 'vue';

import { FORM_FIELD_TOKEN } from './form-field/form-field.token';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
/** `small` is for a field inside a list row or a card, where the full height crowds its neighbours */
const props = withDefaults(defineProps<{ modelValue?: unknown; size?: 'medium' | 'small' }>(), { size: 'medium' });
// Only the value reaches the native input: `size` would otherwise land there as the HTML width attribute
const inputArgs = computed(() => ({ modelValue: props.modelValue, ...attrs }));

const inputRef = ref<HTMLInputElement | null>(null);
const emit = defineEmits(['update:modelValue']);
const formField = inject(FORM_FIELD_TOKEN, {
  id: attrs.id as string,
  hasError: ref(false),
});

function onChange(event: Event): void {
  let value: number | string | null = (event.target as HTMLInputElement).value!;

  if ((event.target as HTMLInputElement).type === 'number') {
    value = value !== '' ? Number(value) : null;
  }

  emit('update:modelValue', value);
}

defineExpose({
  inputElement: inputRef,
});
</script>

<style scoped lang="scss">
.input-wrapper {
  display: inline-flex;
  gap: var(--t-spacing-sm);
  align-items: center;
  height: var(--t-spacing-xxl);

  border: 1px solid var(--t-color-border-primary);
  border-radius: var(--t-border-radius-sm);
  padding-inline: var(--t-spacing-md);

  font: var(--t-typography-body-md-font);
  color: var(--t-color-neutral-700);
  background-color: var(--t-color-neutral-white);

  &:has(input:focus) {
    color: var(--t-color-neutral-950);
    border-color: var(--t-color-neutral-950);
    box-shadow: var(--t-form-field-focus-shadow);
  }

  &.error {
    border-color: var(--t-color-border-danger);
  }

  &.small {
    height: calc(var(--t-spacing-xl) + var(--t-spacing-sm));
    padding-inline: var(--t-spacing-sm);
  }
}

input {
  appearance: none;
  width: 100%;
  height: 100%;

  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  outline: none;
  border: none;
  background: transparent;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: var(--t-color-neutral-500);
  }

  &::-webkit-calendar-picker-indicator {
    color: var(--t-color-neutral-950);
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    border: none;
    -webkit-text-fill-color: inherit;
    -webkit-box-shadow: 0 0 0px 1000px var(--t-color-neutral-white) inset;
  }
}
</style>
